import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, timer } from 'rxjs';
import { map, switchMap, takeUntil, tap, repeat } from 'rxjs/operators';

import { ExternalResponseModel, ExternalsResponse, ExternalsResponseModel, MetaPagingModel } from '@shared/models';
import { EmployeeDetails, ExternalEmployeeExtended    } from '@shared/factories';
import { QueryCollectorService, SessionStorageService } from '@shared/services';

import { environment } from 'environments/environment';

@Injectable()
export class ExternalEmployeesService {
          start$ = new Subject<void>();
  private stop$  = new Subject<void>();

  private get TN_EXTERNAL_EMLOYEES_API(): string { return `${environment.apiUrl}api/portal/v3/external_employees` };
  constructor (
    private http:                  HttpClient,
    private sessionStorageService: SessionStorageService,
    private queryCollectorService: QueryCollectorService
  ) { }

  forceReload(): void {
    this.forceStop();
    this.start$.next();
  }

  forceStop(): void {
    this.stop$.next();
  }

  get externalEmployees(): Observable<EmployeeDetails[]> {
    return timer(0).pipe(
      switchMap(() => this.requestExternalEmployees()),
      takeUntil(this.stop$),
      repeat({ delay: () => this.start$ })
    );
  }

  private requestExternalEmployees(): Observable<EmployeeDetails[]> {
    return this.http.get<any>(`${this.TN_EXTERNAL_EMLOYEES_API}${this.queryCollectorService.getExternalEmployees()}`).pipe(
      tap(res => {
        this.sessionStorageService.changeTotalCount(res?.meta?.paging?.total_count || 0);
        this.sessionStorageService.changeTotalPages(res?.meta?.paging?.total_pages || 0);
      }),
      map(res => res?.external_employees?.map(ext => new EmployeeDetails(ext)) || [])
    );
  }

  loadExternals(page: number = 1, search: string = '', filters: any = {}): Observable<ExternalEmployeeExtended[]> {
    return this.loadExternalsPaginated(page, search, filters).pipe(
      map(res => res.externals)
    );
  }

  loadExternalsPaginated(page: number = 1, search: string = '', filters: any = {}, archive: boolean = false): Observable<ExternalsResponse> {
    return this.http.get<ExternalsResponseModel>(`${this.TN_EXTERNAL_EMLOYEES_API}${archive ? '/archive': ''}${this.queryCollectorService.getExternalsPaginated(page, search, filters)}`)
    .pipe(
      map(res => ({
        totalPages: res?.meta?.paging?.total_pages || 0,
        externals:  res?.external_employees?.map(ext => new ExternalEmployeeExtended(ext)) || []
      }))
    );
  }

  requestEEDashboard<InputType>(eeId: number, useCase: string, query: any = { page: 1, perPage: 5 }): Observable<{ meta: MetaPagingModel, data: InputType }> {
    let queryString = this.isDashboard(useCase) ? this.queryCollectorService.getGeneralQuery(query) : '';
    return this.http.get<any>(`${this.TN_EXTERNAL_EMLOYEES_API}/${eeId}/profile/${this.getDashboardNameByUseCase(useCase)}${queryString}`).pipe(
      map(res => ({
        meta: res?.meta,
        data: res[this.getDashboardNameByUseCase(useCase, true)]
      }))
    );
  }

  private isDashboard(useCase: string): boolean {
    return useCase !== 'info' && useCase !== 'chat';
  }

  private getDashboardNameByUseCase(useCase: string, response: boolean = null): string {
    switch (useCase) {
      case 'info': { return 'general_info';                     };
      case 'chat': { return `${!response ? 'chat_' : '' }room`; };
      case 'ar':   { return 'working_periods';                  };
      case 'vr':   { return 'vacation_requests';                };
      case 'pd':   { return 'photo_documents';                  };
      case 'ebs':  { return 'assignments';                      };
      case 'sr':   { return 'salary_reports';                   };
      case 'mm':   { return 'standalone_mileage_reports';       };
      case 'doc':  { return 'company_documents';                };
      default:
        break;
    };
  }

  loadExternalEmployeeById(eeId: number): Observable<ExternalEmployeeExtended> {
    return this.http.get<ExternalResponseModel>(`${this.TN_EXTERNAL_EMLOYEES_API}/${eeId}${this.queryCollectorService.getExternalsPaginated(1)}`).pipe(
      map(res => new ExternalEmployeeExtended(res?.external_employee))
    );
  }

}
